import React, { FC, ReactElement, useEffect, useState } from 'react';
import { ChooseUniversal, QuarterLoaderUniversal } from '@duxton-web/icons';
import {
  Alert,
  Button,
  Collapse,
  ConfigProvider,
  Flex,
  theme,
  Typography,
} from '@duxton-web/ui';
import { ColumnsType } from '@duxton-web/ui/es/table';

import { DashboardTable } from '@/common/components';
import { STARTED } from '@/constants';
import {
  ExperimentCountry,
  ExperimentEndDate,
  ExperimentNameColumn,
  ExperimentOwner,
  ExperimentStartDate,
  ExperimentStatus,
} from '@/features/experiment/listing/columns';
import Service from '@/lib/App';
import {
  ExperimentView,
  SuperExperiment,
  VariableInfo,
} from '@/lib/PlannerDTOs';

interface ExperimentBlockProps {
  variable: VariableInfo;
}

const ExperimentBlock: FC<ExperimentBlockProps> = ({ variable }) => {
  const [experimentViews, setExperimentViews] = useState<{
    count: number;
    experiment_views: ExperimentView[];
  }>({
    count: 0,
    experiment_views: [],
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [superExperiments, setSuperExperiments] = useState<SuperExperiment[]>(
    [],
  );
  const [expand, setExpand] = useState<boolean>(false);

  const initialColumns: ColumnsType<SuperExperiment> = [
    ExperimentNameColumn,
    ExperimentStatus,
    ExperimentStartDate,
    ExperimentEndDate,
    ExperimentOwner,
    ExperimentCountry,
  ];

  function translateExperimentsToSuperExperiments(data: {
    count: number;
    experiment_views: ExperimentView[];
  }): void {
    if (!data?.experiment_views) {
      setSuperExperiments([]);
      return;
    }

    setExpand(data?.experiment_views?.length > 0);

    setSuperExperiments(
      data.experiment_views.map((experiment_view: ExperimentView) => {
        const model: SuperExperiment = experiment_view.experiment_model
          ? {
              domain: experiment_view.experiment_model.domain,
              name: experiment_view.experiment_model.name,
              state: experiment_view.experiment_model.state,
              variables: experiment_view.experiment_model.variables,
              constraints: experiment_view.experiment_model.constraints,
              owner: experiment_view.experiment_model.owner,
              isOverlap:
                experiment_view.experiment_model?.approvalFlow?.data?.length >
                  1 &&
                experiment_view.experiment_model?.approvalFlow?.resources
                  ?.length > 0
                  ? true
                  : false,
              slotting: experiment_view.experiment_model.slotting,
              metadata: experiment_view.experiment_model.metadata,
            }
          : {
              domain:
                experiment_view.adaptive_experiment_model.experiment_template
                  .domain,
              name: experiment_view.adaptive_experiment_model.name,
              state: experiment_view.adaptive_experiment_model.state,
              variables:
                experiment_view.adaptive_experiment_model.experiment_template
                  .variables,
              constraints:
                experiment_view.adaptive_experiment_model.experiment_template
                  .constraints,
              owner: experiment_view.adaptive_experiment_model.owner,
              isOverlap: false,
            };
        model.type = experiment_view.type;
        model.created_at = experiment_view.created_at;
        model.start_time = experiment_view.start_time;
        model.end_time = experiment_view.end_time;
        return model;
      }),
    );
  }

  async function fetchExperiments(): Promise<void> {
    setLoading(true);

    try {
      const res = await Service.planner.fetchExperimentViewsV2(
        {
          state: [STARTED],
          variable: variable?.name,
        },
        100,
        0,
      );

      setExperimentViews(res);
      translateExperimentsToSuperExperiments(res);
    } catch (e) {
      setExperimentViews({
        count: 0,
        experiment_views: [],
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchExperiments();
  }, []);

  function getExperimentsTitle(): ReactElement {
    if (!loading)
      return (
        <Typography.Text
          strong
        >{`Running Experiments (${superExperiments.length})`}</Typography.Text>
      );

    return (
      <Flex align="center" gap={4}>
        <Typography.Text strong>{`Running Experiments (`}</Typography.Text>
        <QuarterLoaderUniversal />
        <Typography.Text strong>{`Loading... )`}</Typography.Text>
      </Flex>
    );
  }

  function renderExperiments(): ReactElement {
    if (!experimentViews.experiment_views) return null;

    return (
      <Flex vertical gap={16}>
        <ConfigProvider
          theme={{
            components: {
              Collapse: {
                headerBg: '#FFF',
                contentPadding: 0,
              },
              Alert: {
                defaultPadding: '14px 14px',
              },
            },
          }}
        >
          <Collapse
            expandIconPosition="end"
            activeKey={expand ? 'experiments' : ''}
            items={[
              {
                key: 'experiments',
                label: (
                  <Flex align="center" justify="space-between">
                    {getExperimentsTitle()}
                    <Button size="small" ghost />
                  </Flex>
                ),
                onItemClick: (_panelKey: string) => {
                  setExpand(!expand);
                },
                children: (
                  <DashboardTable
                    hideHeader
                    id="experimentblock-experiments"
                    rowClassName="exp-dashboard-v2"
                    columns={initialColumns}
                    rowKey={(record: SuperExperiment) => record.name}
                    dataSource={superExperiments}
                    hideSizeChanger
                    noDataText="Variable is not included in any experiment"
                    pageSize={5}
                    total={experimentViews?.count}
                    loading={loading}
                  />
                ),
              },
            ]}
          />
          <Alert
            showIcon
            type="info"
            message={
              <>
                If a user matches one (or more) of the previously mentioned
                experiments{' '}
                <ChooseUniversal
                  style={{
                    color: theme.defaultConfig.token.yellow,
                  }}
                />
                , they will not be included for any of the following rollouts{' '}
                <ChooseUniversal
                  rotate={180}
                  style={{
                    color: theme.defaultConfig.token.yellow,
                  }}
                />
                .
              </>
            }
          />
        </ConfigProvider>
      </Flex>
    );
  }

  return renderExperiments();
};

export default ExperimentBlock;
